<template>
    <!-- Hero / Header -->
    <header class="selection:bg-brand-500 selection:text-white overflow-x-hidden relative pt-20 pb-24 sm:pt-24 sm:pb-16 border-b border-app">
        <div class="absolute inset-0 -z-10 bg-[radial-gradient(40%_40%_at_50%_50%,rgba(var(--core-brand-500),0.03)_0%,transparent_100%)]">
        </div>
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="text-center mb-10">
                <div class="inline-flex items-center gap-2 rounded-full border border-brand-500/20 bg-brand-500/5 px-4 py-1.5 text-xs font-bold text-brand-600 mb-6 uppercase tracking-wider shadow-sm transition-all hover:bg-brand-500/10">
                    <Megaphone class="h-4 w-4 fill-current" /> Stay Informed
                </div>
                <h1 class="mx-auto max-w-4xl text-5xl font-extrabold tracking-tight text-header sm:text-7xl leading-[1.1]">
                    <span class="bg-gradient-to-r from-brand-500 via-primary-500 to-secondary-500 bg-clip-text text-transparent font-extrabold">News, Programs</span>, and Events
                </h1>
                <p class="mt-8 text-xl text-body/80 leading-relaxed max-w-2xl mx-auto">
                    Discover the latest announcements, ongoing programs, and upcoming events curated just for you.
                </p>
            </div>
        </div>
    </header>

    <!-- Content Tabs -->
    <nav class="sticky top-[72px] z-40 bg-body/80 backdrop-blur-md border-b border-app transition-all">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="flex items-center gap-8 overflow-x-auto no-scrollbar">
                <button v-for="tab in tabs" :key="tab.id" @click="selectTab(tab.id)" :class="[
                    activeTab === tab.id ? 'border-brand-500 text-brand-500' : 'border-transparent text-body/80 hover:text-header hover:border-app',
                    'whitespace-nowrap flex items-center gap-2 py-4 border-b-2 text-sm font-bold transition-all'
                ]">
                    <component :is="tab.icon" class="h-4 w-4" />
                    {{ tab.label }}
                </button>
            </div>
        </div>
    </nav>

    <div id="updates-content-body" ref="bodyRef" class="selection:bg-brand-500 selection:text-white pb-20 overflow-x-hidden min-h-screen scroll-mt-[120px]">
        <!-- Loading State -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-32">
            <div class="h-12 w-12 rounded-full border-4 border-brand-500 border-t-transparent animate-spin mb-4"></div>
            <p class="text-body/80 font-bold text-sm tracking-widest uppercase">Loading latest content...</p>
        </div>

        <!-- Data Sections -->
        <main v-else class="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            
            <!-- Updates Section -->
            <section v-if="activeTab === 'all' || activeTab === 'updates'"
                class="mb-24 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div class="flex items-center justify-between mb-8">
                    <h2 class="text-xl md:text-3xl font-bold text-header flex items-center gap-3">
                        <div class="p-2 rounded-lg bg-orange-500/10 text-orange-600 shadow-inner">
                            <Megaphone class="h-6 w-6" />
                        </div>
                        Latest Updates
                    </h2>
                    <button v-if="activeTab === 'all'" @click="selectTab('updates')"
                        class="text-sm font-bold text-brand-500 hover:text-brand-600 flex items-center gap-1 transition-all hover:translate-x-1 hover:underline">View
                        All
                        <ArrowRight class="h-4 w-4" />
                    </button>
                </div>

                <div v-if="updates.length === 0"
                    class="text-center py-12 border border-app border-dashed rounded-3xl bg-card shadow-sm">
                    <p class="text-body/80 text-sm font-bold">No updates found.</p>
                </div>
                <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <template v-for="update in (activeTab === 'all' ? updates.slice(0, 3) : updates)" :key="update.id">
                        <UpdateContent :item="update" />
                    </template>
                </div>
            </section>

            <!-- Programs Section -->
            <section v-if="activeTab === 'all' || activeTab === 'programs'"
                class="mb-24 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
                <div class="flex items-center justify-between mb-8">
                    <h2 class="text-xl md:text-3xl font-bold text-header flex items-center gap-3">
                        <div class="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 shadow-inner">
                            <Target class="h-6 w-6" />
                        </div>
                        Active Programs
                    </h2>
                    <button v-if="activeTab === 'all'" @click="selectTab('programs')"
                        class="text-sm font-bold text-brand-500 hover:text-brand-600 flex items-center gap-1 transition-all hover:translate-x-1 hover:underline">View
                        All
                        <ArrowRight class="h-4 w-4" />
                    </button>
                </div>

                <div v-if="programs.length === 0"
                    class="text-center py-12 border border-app border-dashed rounded-3xl bg-card shadow-sm">
                    <p class="text-body/80 text-sm font-bold">No programs currently available.</p>
                </div>
                <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <template v-for="program in (activeTab === 'all' ? programs.slice(0, 2) : programs)" :key="program.id">
                        <ProgramContent :item="program" />
                    </template>
                </div>
            </section>

            <!-- Events Section -->
            <section v-if="activeTab === 'all' || activeTab === 'events'"
                class="animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
                <div class="flex items-center justify-between mb-8">
                    <h2 class="text-xl md:text-3xl font-bold text-header flex items-center gap-3">
                        <div class="p-2 rounded-lg bg-blue-500/10 text-blue-600 shadow-inner">
                            <Calendar class="h-6 w-6" />
                        </div>
                        Upcoming Events
                    </h2>
                    <button v-if="activeTab === 'all'" @click="selectTab('events')"
                        class="text-sm font-bold text-brand-500 hover:text-brand-600 flex items-center gap-1 transition-all hover:translate-x-1 hover:underline">View
                        All
                        <ArrowRight class="h-4 w-4" />
                    </button>
                </div>

                <div v-if="events.length === 0"
                    class="text-center py-12 border border-app border-dashed rounded-3xl bg-card shadow-sm">
                    <p class="text-body/80 text-sm font-bold">No upcoming events scheduled.</p>
                </div>
                <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <template v-for="event in (activeTab === 'all' ? events.slice(0, 3) : events)" :key="event.id">
                        <EventContent :item="event" />
                    </template>
                </div>
            </section>

        </main>

    </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import {
    Megaphone,
    Calendar,
    Target,
    LayoutGrid,
    MapPin,
    ArrowRight,
    Clock,
} from 'lucide-vue-next';
import { fetchUpdates } from '~/utils/api';

import UpdateContent from '~/components/updates/UpdateContent.vue';
import ProgramContent from '~/components/updates/ProgramContent.vue';
import EventContent from '~/components/updates/EventContent.vue';

definePageMeta({ name: 'Updates' });

// Data models
interface Update { id: string; title: string; content: string; imageUrl: string | null; memberOnly: boolean; status: string; createdAt: string; updatedAt: string; }
interface Program { id: string; name: string; description: string; imageUrl: string | null; startDate: string; endDate: string; isActive: boolean; }
interface Event { id: string; title: string; description: string; imageUrl: string | null; startDate: string; endDate: string; location: string; status: string; }

// State
// Fetched during the server render; client-side navigation shows the loader until it arrives.
const { data, status } = useAsyncData('updates', () => fetchUpdates(), { lazy: true });
const updates = computed<Update[]>(() => data.value?.updates || []);
const programs = computed<Program[]>(() => data.value?.programs || []);
const events = computed<Event[]>(() => data.value?.events || []);
const loading = computed(() => status.value === 'pending');
const activeTab = ref('all');
const bodyRef = ref<HTMLElement | null>(null);

const selectTab = (id: string) => {
    activeTab.value = id;
    if (history.replaceState) {
        history.replaceState(null, '', `#${id}`);
    } else {
        window.location.hash = id;
    }
    nextTick(() => {
        bodyRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
};

const applyHash = () => {
    const id = window.location.hash.replace('#', '');
    if (id && tabs.some(t => t.id === id)) {
        activeTab.value = id;
    }
};

// Helper functions for dates since $filter.day might just return ISO or localized simple string.
const getMonth = (isoDate: string) => {
    if (!isoDate) return '';
    try {
        const d = new Date(isoDate);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleString('default', { month: 'short' });
    } catch (e) { return ''; }
}
const getDayNum = (isoDate: string) => {
    if (!isoDate) return '';
    try {
        const d = new Date(isoDate);
        if (isNaN(d.getTime())) return '';
        return d.getDate();
    } catch (e) { return ''; }
}
const formatTime = (isoDate: string) => {
    if (!isoDate) return '';
    try {
        const d = new Date(isoDate);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) { return ''; }
}

// Tabs config
const tabs = [
    { id: 'all', label: 'Overview', icon: LayoutGrid },
    { id: 'updates', label: 'News & Updates', icon: Megaphone },
    { id: 'programs', label: 'Programs', icon: Target },
    { id: 'events', label: 'Events', icon: Calendar },
];
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
    display: none;
}

.no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.bg-body {
    background-color: rgb(var(--core-body-bg));
}

.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}
</style>
